import { useMemo } from "react";
import { FaRegEdit } from "react-icons/fa";
import { HiOutlineTrash } from "react-icons/hi2";
import { IHomeTileConfig } from "@/api_framework/api_modals/HomeTileConfig";
import { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useUtils } from "@/app/hooks/useUtils";
import { PersonaExpressionText, RibbonPreview, StatusBadge } from "../components";
import { STATUS_COLUMN_FILTER_OPTIONS, StatusFilter } from "../interface";
import { fromPersonaRule } from "../personaExpression";

const EmptyCell = () => <span className="text-xs text-slate-400">—</span>;

export const useConfigColumns = ({
  onEdit,
  onDelete,
  statusFilter,
  onStatusFilterChange,
}: {
  onEdit: (configId: string) => void;
  onDelete: (config: IHomeTileConfig) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (next: StatusFilter) => void;
}): TableColumn<IHomeTileConfig>[] => {
  const { veticDateFormat } = useUtils();

  return useMemo<TableColumn<IHomeTileConfig>[]>(
    () => [
      {
        id: "serial_number",
        header: "S.No",
        width: 70,
        cell: ({ rowIndex }) => <span className="text-xs tabular-nums text-slate-600">{rowIndex + 1}</span>,
        headerClassName: "text-slate-500",
      },
      {
        id: "persona_combination",
        header: "Persona Combination",
        minWidth: 280,
        cell: ({ row }) => {
          const combination = fromPersonaRule(row.persona_rule);
          return combination ? (
            <PersonaExpressionText value={combination} />
          ) : (
            <span className="text-xs text-slate-400">Dynamic only</span>
          );
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "ribbon_text",
        header: "Ribbon Text",
        width: 160,
        cell: ({ row }) =>
          row.ribbon_text ? (
            <RibbonPreview text={row.ribbon_text} style={row.ribbon} isActive={row.status === "ACTIVE"} />
          ) : (
            <EmptyCell />
          ),
        headerClassName: "text-slate-500",
      },
      {
        id: "status",
        header: "Status",
        width: 130,
        overflow: "visible",
        filter: {
          options: STATUS_COLUMN_FILTER_OPTIONS,
          value: statusFilter,
          onChange: (next) => onStatusFilterChange(next as StatusFilter),
        },
        cell: ({ row }) => <StatusBadge status={row.status} />,
        headerClassName: "text-slate-500",
      },
      {
        id: "created_at",
        header: "Created At",
        width: 170,
        cell: ({ row }) => {
          const createdAt = veticDateFormat(row.created_at);
          return createdAt ? (
            <span className="whitespace-nowrap text-xs text-slate-600">{createdAt}</span>
          ) : (
            <EmptyCell />
          );
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "updated_at",
        header: "Updated At",
        width: 170,
        cell: ({ row }) => {
          const updatedAt = veticDateFormat(row.updated_at);
          return updatedAt ? (
            <span className="whitespace-nowrap text-xs text-slate-600">{updatedAt}</span>
          ) : (
            <EmptyCell />
          );
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "updated_by",
        header: "Updated By",
        minWidth: 180,
        cell: ({ row }) =>
          row.updated_by ? (
            <span className="block truncate text-xs text-slate-600" title={row.updated_by}>
              {row.updated_by}
            </span>
          ) : (
            <EmptyCell />
          ),
        headerClassName: "text-slate-500",
      },
      {
        id: "action",
        header: "Action",
        align: "center",
        width: 130,
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(row.id)}
              className="rounded-lg border border-transparent p-2 text-[#193A6A] transition-colors hover:border-slate-200 hover:bg-slate-50"
              aria-label={`Edit configuration ${row.id}`}
              title="Edit configuration"
            >
              <FaRegEdit className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(row)}
              className="rounded-lg border border-transparent p-2 text-rose-600 transition-colors hover:border-rose-100 hover:bg-rose-50"
              aria-label={`Delete rule ${row.id}`}
              title="Delete rule"
            >
              <HiOutlineTrash className="h-4 w-4" />
            </button>
          </div>
        ),
        headerClassName: "text-slate-500 text-center",
      },
    ],
    [onEdit, onDelete, statusFilter, onStatusFilterChange, veticDateFormat],
  );
};
